import { chain } from 'obliterator';

import { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody } from '../loaders/body';
import { areObjectsDifferentForKeys } from '../utils';
import type { AnyIntersectionTest } from './intersection-utils';
import { getBodyPose } from './load-utils';
import type { Renderer, RendererState } from './renderer';
import { VECTOR_EDITOR_MODES } from './renderer-component';

export const getAllAffectedBodies = (
  bodyOrAssemblyInstanceById: RendererState['bodyOrAssemblyInstanceById'],
  bodyOrAssemblyInstanceIds: Iterable<string>,
): IterableIterator<AnyBody> =>
  (function* () {
    for (const bodyOrAssemblyInstanceId of bodyOrAssemblyInstanceIds) {
      if (bodyOrAssemblyInstanceId) {
        const bodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(
          bodyOrAssemblyInstanceId,
        );
        if (bodyOrAssemblyInstance instanceof AssemblyInstance) {
          yield* bodyOrAssemblyInstance.descendentBodies();
        } else if (bodyOrAssemblyInstance) {
          yield bodyOrAssemblyInstance;
        }
      }
    }
  })();

export const getAllAffectedBodyIds = (
  bodyOrAssemblyInstanceById: RendererState['bodyOrAssemblyInstanceById'],
  bodyOrAssemblyInstanceIds: Iterable<string>,
): Set<string> => {
  return new Set<string>(
    (function* () {
      for (const body of getAllAffectedBodies(
        bodyOrAssemblyInstanceById,
        bodyOrAssemblyInstanceIds,
      )) {
        if (body.id) {
          yield body.id;
        }
      }
    })(),
  );
};

export const setVisibilityForObjectIds = async (
  state: RendererState,
  visibleBodyOrAssemblyInstanceIds: Iterable<string>,
): Promise<void> => {
  const { assemblyInstances, bodies, bodyOrAssemblyInstanceById } = state;

  let changed = false;

  // first set all assembly instances visibility to true
  for (const { group } of assemblyInstances) {
    if (!group.visible) {
      group.visible = true;
      changed = true;
    }
  }

  // now recursively collect children that need to be set to visible
  const affectedBodyIds = getAllAffectedBodyIds(
    bodyOrAssemblyInstanceById,
    visibleBodyOrAssemblyInstanceIds,
  );
  for (const body of bodies) {
    const hasBody = affectedBodyIds.has(body.id as string);
    if (body.group.visible !== hasBody) {
      body.group.visible = hasBody;
      changed = true;
    }
    body.additionalFeedbackGroup.visible = hasBody;
  }

  if (changed) {
    await state.onBodyPoseChange(
      getBodyPose(bodyOrAssemblyInstanceById.values()),
    );
  }

  state.updatePartPMIVisibility(state);
};

export const setVisibilityForFocus = async (
  state: RendererState,
): Promise<void> => {
  const { focusedBodies } = state;
  // if no focused bodies do not do anything
  if (!focusedBodies.length) return;

  await setVisibilityForObjectIds(state, focusedBodies);
};

export const handleUpdateVisibleBodies = (
  renderer: Renderer,
  nextState: RendererState,
  force?: boolean,
): boolean => {
  const requiresUpdate =
    force ||
    areObjectsDifferentForKeys(renderer.state, nextState, [
      'visibleBodies',
      'visibleStockResultKeys',
    ]);
  if (requiresUpdate) {
    setVisibilityForObjectIds(
      nextState,
      chain(nextState.visibleBodies, nextState.visibleStockResultKeys),
    );
    // Dispose of active measurements when visibleBodies change
    const measurement = renderer.state.measurement;
    if (
      measurement &&
      // In new Vector Editor, do not dispose of measurement on VB change as VB change is triggered on activeTool change
      // This allows the measurement to persist during the new click-shift-click measurement feature
      renderer.state.editorMode !== VECTOR_EDITOR_MODES.SCALE
    ) {
      renderer.scene2.remove(measurement.group);
      measurement.dispose();
      // call onObjectClickForCaliperTool to deselect caliper objects on forced measurement dispose
      renderer.state.onObjectClickForCaliperTool({} as AnyIntersectionTest);
    }
    return true;
  } else {
    return false;
  }
};
